import { createRootRoute, createRoute, createRouter, redirect } from "@tanstack/react-router";
import React from "react";
import { AppShell } from "./components/app-shell";

function lazyRoute(loader: () => Promise<{ default: React.ComponentType }>) {
  const Component = React.lazy(loader);
  // Keep route modules split from the shell while AppShell owns the Suspense fallback for every Outlet.
  return function LazyRouteComponent() {
    return React.createElement(Component);
  };
}

const WorkbenchPage = lazyRoute(() =>
  import("./pages/workbench-page").then((m) => ({ default: m.WorkbenchPage })),
);
const LoginPage = lazyRoute(() =>
  import("./pages/login-page").then((m) => ({ default: m.LoginPage })),
);
const HelpPage = lazyRoute(() =>
  import("./pages/help-page").then((m) => ({ default: m.HelpPage })),
);
const HandoffPage = lazyRoute(() =>
  import("./pages/handoff-page").then((m) => ({ default: m.HandoffPage })),
);

const SettingsLayout = React.lazy(() =>
  import("./pages/settings-pages").then((m) => ({ default: m.SettingsLayout })),
);
const SettingsProfilePage = React.lazy(() =>
  import("./pages/settings-pages").then((m) => ({ default: m.SettingsProfilePage })),
);
const SettingsSecurityPage = React.lazy(() =>
  import("./pages/settings-pages").then((m) => ({ default: m.SettingsSecurityPage })),
);
const SettingsBillingPage = React.lazy(() =>
  import("./pages/settings-pages").then((m) => ({ default: m.SettingsBillingPage })),
);
const SettingsOrganizationsPage = React.lazy(() =>
  import("./pages/settings-pages").then((m) => ({ default: m.SettingsOrganizationsPage })),
);

type RouteStaticData = { title: string; subtitle: string };

const rootRoute = createRootRoute({
  component: AppShell,
});

const workbenchRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: WorkbenchPage,
  staticData: {
    title: "Workbench",
    subtitle: "A focused space for cross-language refinement",
  } satisfies RouteStaticData,
});

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings",
  component: SettingsLayout,
  staticData: {
    title: "Settings",
    subtitle: "Manage your account",
  } satisfies RouteStaticData,
});

const settingsIndexRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "/",
  // Visiting /settings directly must land on a concrete pane instead of rendering an empty Outlet.
  beforeLoad: () => {
    throw redirect({ to: "/settings/profile" });
  },
});

const settingsProfileRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "/profile",
  component: SettingsProfilePage,
  staticData: {
    title: "Settings",
    subtitle: "Review your account and move into the right settings area",
  } satisfies RouteStaticData,
});

const settingsSecurityRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "/security",
  component: SettingsSecurityPage,
  staticData: {
    title: "Settings",
    subtitle: "Manage sign-in methods and active sessions",
  } satisfies RouteStaticData,
});

const settingsBillingRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "/billing",
  component: SettingsBillingPage,
  staticData: {
    title: "Settings",
    subtitle: "Review plans, entitlements, and subscription actions",
  } satisfies RouteStaticData,
});

const settingsOrganizationsRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "/organizations",
  component: SettingsOrganizationsPage,
  staticData: {
    title: "Settings",
    subtitle: "Choose the active workspace and manage invitations",
  } satisfies RouteStaticData,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
});

const helpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/help",
  component: HelpPage,
  staticData: {
    title: "Help",
    subtitle: "Shortcuts, recovery paths, and where to manage blocked workflows",
  } satisfies RouteStaticData,
});

const handoffRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/open",
  component: HandoffPage,
});

function redirectToCanonicalRoute(to: "/" | "/settings/billing" | "/open") {
  return ({ location }: { location: { search: Record<string, unknown> } }) => {
    // ponytail: root aliases only canonicalize known product entrypoints; add a real route if one gets distinct UI.
    throw redirect({ to, search: location.search });
  };
}

const workbenchAliasRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/workbench",
  beforeLoad: redirectToCanonicalRoute("/"),
});

const billingAliasRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/billing",
  beforeLoad: redirectToCanonicalRoute("/settings/billing"),
});

const handoffAliasRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/handoff",
  beforeLoad: redirectToCanonicalRoute("/open"),
});

const routeTree = rootRoute.addChildren([
  workbenchRoute,
  settingsRoute.addChildren([
    settingsIndexRoute,
    settingsProfileRoute,
    settingsSecurityRoute,
    settingsBillingRoute,
    settingsOrganizationsRoute,
  ]),
  loginRoute,
  helpRoute,
  handoffRoute,
  workbenchAliasRoute,
  billingAliasRoute,
  handoffAliasRoute,
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
